import React, { useEffect, useState } from "react";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import { Col, Row } from "react-bootstrap";
import {
  Button,
  Checkbox,
  createTheme,
  FormControlLabel,
  MuiThemeProvider,
  TextField,
} from "@material-ui/core";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useHistory, useParams } from "react-router-dom";
// import { deleteTherapist, getTherapistById, postTherapist, updateTherapist } from "../../../../api/therapist";
import { useSkeleton } from "../../../hooks/useSkeleton";
import {
  deleteUser,
  getUserById,
  postUser,
  sendVerifyUserMailAndRefresh,
  updateUser,
} from "../../../../api/user";
import { grantPermissionsCalendar } from "../../../../api/calendar";
import AvailabilityCard from "./AvailabilityCard";
import {
  isGoogleCalendarTokenExpiredError,
  promptReconnectGoogleCalendar,
} from "../../../../utils/googleCalendarAuth";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyTherapist() {
  return {
    active: true,
    isTherapist: true,
    isAdmin: false,
    acronym: "",
  };
}

function isValidAcronym(acronym) {
  return /^[A-Z]{3}$/.test((acronym || "").trim().toUpperCase());
}

export default function EditTherapistPage() {
  const therapistId = useParams().id;
  const history = useHistory();
  const [therapist, setTherapist] = useState(getEmptyTherapist());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(0);
  const [refresh, setRefresh] = useState(null);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    setRefresh(false);
  }, [refresh]);

  useEffect(() => {
    fetch();
  }, []);

  function fetch() {
    if (!therapistId) {
      disableLoadingData();
      return;
    }
    getUserById(therapistId)
      .then((res) => {
        const therapist = res.data;
        delete therapist.password;
        setTherapist(therapist);
        disableLoadingData();
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido encontrar el terapeuta.",
        });
      });
  }

  function saveTherapist() {
    const normalizedAcronym = (therapist?.acronym || "").trim().toUpperCase();
    if (!isValidAcronym(normalizedAcronym)) {
      alertError({
        error: null,
        customMessage:
          "El acrónimo es obligatorio y debe tener exactamente 3 letras.",
      });
      return;
    }

    const therapistToSave = { ...therapist, acronym: normalizedAcronym };

    if (!therapist?._id) {
      postUser(therapistToSave)
        .then((res) => {
          // El aviso de éxito lo da el diálogo de envío de mails, para no mostrar dos ventanas a la vez
          setTherapist({
            ...therapist,
            _id: res?.data?.id,
            verificationToken: res?.data?.token,
          });
          setOpenConfirmDialog(4);
          // history.push("/therapists");
        })
        .catch((error) => {
          alertError({
            error,
            customMessage: "No se ha podido crear el terapeuta.",
          });
        });
    } else {
      updateUser(therapist._id, therapistToSave)
        .then((res) => {
          alertSuccess({
            title: "Éxito!",
            customMessage: "Terapeuta actualizado correctamente.",
          });
          history.push("/therapists");
        })
        .catch((error) => {
          alertError({ error, customMessage: "No se ha podido actualizar el terapeuta." });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setTherapist({ ...therapist, [element]: event.target.value });
  };

  const handleAcronymChange = (event) => {
    const value = event.target.value
      .toUpperCase()
      .replace(/[^A-Z]/g, "")
      .slice(0, 3);
    setTherapist({ ...therapist, acronym: value });
  };

  function permissionsCalendar() {
    grantPermissionsCalendar(
      therapist?._id,
      therapist?.fullName,
      therapist?.surname,
      therapist?.email,
    )
      .then((res) => {
        alertSuccess({ message: "Mail de Calendar enviado correctamente!" });
      })
      .catch((error) => {
        if (isGoogleCalendarTokenExpiredError(error)) {
          promptReconnectGoogleCalendar(history);
          return;
        }
        alertError({ error, message: "No se ha podido enviar el mail." });
      });
  }

  function verificationEmail() {
    sendVerifyUserMailAndRefresh(therapist?.verificationToken)
      .then((res) => {
        alertSuccess({
          title: "Éxito!",
          customMessage: "Mail de verificación enviado correctamente.",
        });
      })
      .catch((error) => {
        alertError({ error, customMessage: "No se ha podido enviar el mail." });
      });
  }

  // Al crear el terapeuta solo se envía el mail de verificación. La invitación al Calendar
  // compartido se envía sola cuando el terapeuta guarda la URL de su página de reserva
  // (paso 3 de la guía); si se enviara ahora, ese paso quedaría marcado como hecho.
  function sendWelcomeEmail() {
    sendVerifyUserMailAndRefresh(therapist?.verificationToken)
      .then(() => {
        alertSuccess({
          title: "Éxito!",
          customMessage:
            "Terapeuta creado correctamente. Mail de verificación enviado.",
        });
      })
      .catch(() => {
        alertError({
          error:
            "Terapeuta creado correctamente, pero NO se ha podido enviar el mail de verificación.",
        });
      });
  }

  if (isLoadingData) return <ContentSkeleton />;
  return (
    <>
      <Card>
        <CardHeader title="Editar terapeuta" />
        <CardBody>
          <Row>
            <Col>
              <TextField
                label="Nombre"
                value={therapist?.fullName}
                onChange={handleChange("fullName")}
                margin="normal"
                variant="outlined"
                required
              />
            </Col>
            <Col>
              <TextField
                label="Apellidos"
                value={therapist?.surname}
                onChange={handleChange("surname")}
                margin="normal"
                variant="outlined"
                required
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextField
                label="Email"
                value={therapist?.email}
                onChange={handleChange("email")}
                margin="normal"
                variant="outlined"
                required
              />
            </Col>
            <Col>
              <TextField
                label="Teléfono"
                value={therapist?.phone}
                onChange={handleChange("phone")}
                margin="normal"
                variant="outlined"
                required
              />
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <TextField
                label="Acrónimo"
                value={therapist?.acronym || ""}
                onChange={handleAcronymChange}
                margin="normal"
                variant="outlined"
                required
                inputProps={{
                  maxLength: 3,
                  style: { textTransform: "uppercase" },
                }}
                helperText="3 letras, ej. BAR, TON"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={therapist?.active}
                    onChange={() =>
                      setTherapist({ ...therapist, active: !therapist?.active })
                    }
                    name="checkActive"
                  />
                }
                label="Activo"
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
      {therapist?._id && <AvailabilityCard therapistId={therapist._id} />}
      <Button
        onClick={() => history.push("/therapists")}
        variant="outlined"
        style={{ marginRight: "20px" }}
      >
        Atrás
      </Button>
      <Button
        onClick={() => saveTherapist()}
        variant="outlined"
        color="primary"
        style={{ marginRight: "20px" }}
      >
        Guardar terapeuta
      </Button>
      {therapist?._id && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button
              onClick={() => setOpenConfirmDialog(1)}
              variant="outlined"
              color="secondary"
              style={{ marginRight: "20px" }}
            >
              Eliminar terapeuta
            </Button>
          </MuiThemeProvider>

          <ConfirmDialog
            title={"Seguro que quieres eliminar el terapeuta?"}
            open={openConfirmDialog === 1}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteUser(therapist._id)
                .then((res) => {
                  alertSuccess({
                    title: "Éxito!",
                    customMessage: "Terapeuta eliminado correctamente.",
                  });
                  history.push("/therapists");
                })
                .catch((error) => {
                  alertError({
                    error,
                    customMessage: "No se ha podido eliminar el terapeuta.",
                  });
                });
            }}
          />

          {!therapist?.password && (
            <Button
              onClick={() => setOpenConfirmDialog(2)}
              variant="outlined"
              style={{ marginRight: "20px" }}
            >
              Enviar mail de confirmación
            </Button>
          )}

          <ConfirmDialog
            title={
              "Seguro que quieres enviar un mail de verificación al terapeuta?"
            }
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              verificationEmail();
            }}
          />

          {!therapist?.calendarId && (
            <Button
              onClick={() => setOpenConfirmDialog(3)}
              variant="outlined"
              style={{ marginRight: "20px" }}
            >
              Enviar permisos google calendar
            </Button>
          )}

          <ConfirmDialog
            title={
              "Seguro que quieres enviar el email con la garantización de los permisos para el Calendar compartido al terapueta?"
            }
            open={openConfirmDialog === 3}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              permissionsCalendar();
            }}
          />

          <ConfirmDialog
            title={
              "Terapeuta creado. ¿Quieres enviarle ahora el email de verificación de su cuenta?"
            }
            open={openConfirmDialog === 4}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              sendWelcomeEmail();
            }}
          />
        </>
      )}
    </>
  );
}
